<template>
  <!-- MYAC-1183 — one "way to start" card of an empty state: an icon, a
       heading, one sentence, and the action pinned to the bottom so sibling
       cards in the same grid keep their CTAs on one line whatever the body
       length. The Monitoring list and Suggestions to add empty states both compose it; the
       Connect-my-TD block (`ConnectTdButton.vue`, variant "block") is one of
       its instances. The card never decides anything: gating and behaviour
       stay with the host, this is the frame. -->
  <!-- `bare` (MYAC-1522): no card of its own, for a host that is already the frame
       (the chat's « nothing to look at yet », inside the chat panel's card). -->
  <div
    class="empty-state-block flex flex-col gap-2 h-full"
    :class="bare ? 'is-bare' : ['p-5 border rounded-xl', highlighted ? 'border-primary shadow-sm' : 'border-surface']"
  >
    <span
      class="flex items-center justify-center w-9 h-9 rounded-lg bg-primary-50 text-primary mb-1"
      aria-hidden="true"
    >
      <i :class="icon" />
    </span>
    <h3 class="m-0 text-base font-semibold">{{ title }}</h3>
    <p class="m-0 text-sm text-muted-color">{{ body }}</p>
    <!-- MYAC-1306 — one CTA rule for every empty state of the module
         (Aymeric, 23/09): full-width buttons, stacked, icon on the left; the
         highlighted card's first action is the filled one, everything else
         outlined (the host picks severity/outlined, the frame sets width). -->
    <div class="empty-state-cta mt-auto pt-3 flex flex-col gap-2">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
defineProps<{
  /** PrimeIcons class, e.g. `pi pi-pencil`. */
  icon: string
  title: string
  body: string
  /** The path we lean the user toward gets the primary border. */
  highlighted?: boolean
  /** No border, padding or ground: the host is the frame. */
  bare?: boolean
}>()
</script>

<style scoped>
/* A card of its own, white on any ground: `surface-card` is not a class this
   app defines, so the block was transparent, invisible on the grey dashboard
   (MYAC-1522; inside the watch pages' white panel it changes nothing). */
.empty-state-block:not(.is-bare) { background: var(--p-surface-0); }
/* Whatever wraps the button (CreditGate, a tooltip span, the admin tooltip),
   the action spans the card: same width, same place, on every page. */
/* (A button is left as PrimeVue lays it out — inline-flex keeps the gap
   between its icon and its label; only its width is set.) */
.empty-state-cta > :slotted(*:not(.p-button)) {
  display: block;
  width: 100%;
}
.empty-state-cta :deep(.p-button) {
  width: 100%;
}
</style>
